import React, { useState } from 'react';
import { ExternalLink, Tag, Calendar, Store, ArrowRight } from 'lucide-react';

/**
 * Compact Editorial DealBox - 120-150px height
 * Premium magazine-style design for deal highlights
 */
export const DealBox = ({ deal, variant = 'default' }) => {
  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    setClicked(true);
    setTimeout(() => setClicked(false), 2000);
  };

  // Inline variant - ultra compact for within article content
  if (variant === 'inline') {
    return (
      <div className="my-6 bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-red hover:shadow-md smooth-transition" data-testid="deal-box-inline">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-gray-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xs text-gray-500 uppercase tracking-wide">{deal.store_name}</div>
              <div className="font-semibold text-gray-900 truncate" data-testid="offer-title">{deal.offer_title}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="bg-brand-red/10 text-brand-red px-3 py-1 rounded-full text-sm font-bold" data-testid="discount-badge">
              {deal.discount}
            </span>
            <a
              href={deal.button_link}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={handleClick}
              className="bg-brand-red hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold smooth-transition flex items-center gap-1"
              data-testid="deal-button"
            >
              {clicked ? 'Opening...' : deal.button_text || 'Get Deal'}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Featured variant - slightly larger but still compact
  if (variant === 'featured') {
    return (
      <div className="my-8 bg-gradient-to-r from-brand-green to-emerald-700 rounded-2xl p-6 text-white" data-testid="deal-box-featured">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                Featured Deal
              </span>
              <span className="bg-brand-yellow text-gray-900 px-3 py-1 rounded-full text-sm font-black" data-testid="discount-badge">
                {deal.discount}
              </span>
            </div>
            <h4 className="text-xl font-playfair font-bold mb-1" data-testid="offer-title">{deal.offer_title}</h4>
            <p className="text-gray-200 text-sm">{deal.store_name}</p>
            {deal.expiry_date && (
              <div className="flex items-center gap-1 text-gray-300 text-xs mt-2">
                <Calendar className="w-3 h-3" />
                <span data-testid="expiry-date">Expires {deal.expiry_date}</span>
              </div>
            )}
          </div>
          <a
            href={deal.button_link}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleClick}
            className="flex-shrink-0 bg-white hover:bg-gray-100 text-brand-green px-6 py-3 rounded-xl font-bold smooth-transition flex items-center gap-2"
            data-testid="deal-button"
          >
            {clicked ? 'Opening...' : deal.button_text || 'Get Deal'}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  // Default - compact editorial card (~120-150px)
  return (
    <div className="my-6 bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg smooth-transition group" data-testid="deal-box">
      <div className="flex items-stretch">
        {/* Left: Store/Brand */}
        <div className="flex-shrink-0 w-20 bg-gray-50 flex flex-col items-center justify-center p-4 border-r border-gray-100">
          <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-2 group-hover:shadow-md smooth-transition">
            <Store className="w-6 h-6 text-gray-600" />
          </div>
          <span className="text-xs text-gray-600 font-medium text-center leading-tight" data-testid="store-name">
            {deal.store_name}
          </span>
        </div>

        {/* Center: Deal Info */}
        <div className="flex-1 p-4 flex flex-col justify-center min-w-0">
          <h4 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-brand-red smooth-transition" data-testid="offer-title">
            {deal.offer_title}
          </h4>
          {deal.expiry_date && (
            <div className="flex items-center gap-1 text-gray-500 text-xs">
              <Calendar className="w-3 h-3" />
              <span data-testid="expiry-date">Valid until {deal.expiry_date}</span>
            </div>
          )}
        </div>

        {/* Right: Discount + CTA */}
        <div className="flex-shrink-0 flex items-center gap-3 p-4 pl-0">
          <div className="text-right">
            <div className="bg-brand-red/10 text-brand-red px-3 py-1.5 rounded-lg font-black text-lg" data-testid="discount-badge">
              {deal.discount}
            </div>
          </div>
          <a
            href={deal.button_link}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleClick}
            className="bg-brand-red hover:bg-red-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm smooth-transition flex items-center gap-1 whitespace-nowrap"
            data-testid="deal-button"
          >
            <Tag className="w-4 h-4" />
            {clicked ? '...' : 'Get'}
          </a>
        </div>
      </div>

      {/* Affiliate disclosure - minimal */}
      <div className="bg-gray-50 px-4 py-1.5 border-t border-gray-100">
        <p className="text-[10px] text-gray-400 text-center">Affiliate link • We may earn commission</p>
      </div>
    </div>
  );
};

export default DealBox;
